import {
  HeatMapCanvasProps,
  HeatMapSerie,
  ResponsiveHeatMapCanvas,
} from '@nivo/heatmap';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { TwTheme } from '../../../Tw';
import {
  commonAxisConfig,
  commonMarginConfig,
  commonThemeConfig,
} from '../../common/utils/chartConfig';
import ContainerChart from '../../simulation/panels/ContainerChart';
import useActualityAggregationFieldDescriptors from '../hooks/useActualityAggregationFieldDescriptors';
import useGetActualityAggregationMetricValue from '../hooks/useGetActualityAggregationMetricValue';
import { getAggregatedStatsKey } from '../store/actuality.kpi.helper';
import { actualityAggregatedStatsSelectedSubGroup } from '../store/actuality.kpi.state';
import {
  ActualityAggregatedHqExtendedDataRow,
  ActualityAggregationField,
} from '../store/actuality.kpi.types';
import {
  actualityAggregatedStatsColorBySelected,
  actualityAggregatedStatsViewModeSelected,
} from '../store/actuality.kpi.ui.state';
import ActualityAggregatedStatChartTooltip from './ActualityAggregatedStatChartTooltip';
import { formatChartAxisLabel } from './chartUtils';

const colors = TwTheme.extend.colors;
const theme = TwTheme.extend.colors.chart;

type ChartProps = Omit<
  HeatMapCanvasProps<ActualityAggregatedStatsTimelineHeatmapItem, {}>,
  'width' | 'height'
>;

export type ActualityAggregatedStatsTimelineHeatmapItem =
  ActualityAggregatedHqExtendedDataRow & {
    _key: Partial<ActualityAggregatedHqExtendedDataRow>;
    _color: string;
    _groupColor: string;
    x: number;
    y: number;
    _type?: 'placeholder' | 'gap';
  };

export type ActualityAggregatedStatsHeatmapChartDataItem = HeatMapSerie<
  ActualityAggregatedStatsTimelineHeatmapItem,
  {}
>;

export type ActualityAggregatedStatsHeatmapChartProps = {
  chartData: ActualityAggregatedStatsHeatmapChartDataItem[];
  xAxisField: ActualityAggregationField;
  yAxisField: ActualityAggregationField;
  ticksValues: Set<String>;
  showAxisY?: boolean;
  showAxisX?: boolean;
  cellHeight?: number; // Add cellHeight prop to control individual cell height
  compareMode?: boolean;
  fullHeight?: boolean;
};

const ActualityAggregatedStatsHeatmapChart: React.FC<
  ActualityAggregatedStatsHeatmapChartProps
> = props => {
  const { t } = useTranslation('feed');
  const viewMode = useAtomValue(actualityAggregatedStatsViewModeSelected);
  const aggFieldsDescriptors = useActualityAggregationFieldDescriptors();
  const getMetricValue = useGetActualityAggregationMetricValue();
  const colorBy = useAtomValue(actualityAggregatedStatsColorBySelected);
  const xAxisFieldDescriptor = aggFieldsDescriptors[props.xAxisField];
  const yAxisFieldDescriptor = aggFieldsDescriptors[props.yAxisField];
  const [selectedSubgroup, setSelectedSubgroup] = useRecoilState(
    actualityAggregatedStatsSelectedSubGroup,
  );

  // Use shared date formatting function with field descriptors
  // Each axis uses its own field key and descriptor for proper formatting

  function onClick(item: ActualityAggregatedStatsTimelineHeatmapItem) {
    setSelectedSubgroup(
      getAggregatedStatsKey(item, [props.xAxisField, props.yAxisField]),
    );
  }

  const heatMapCanvasProps: ChartProps = {
    data: props.chartData,

    margin: {
      ...commonMarginConfig,
      left: props.fullHeight ? 0 : commonMarginConfig.left,
    },
    axisTop:
      viewMode === 'heatmap' && props.showAxisX
        ? {
            ...commonAxisConfig,
            format: value =>
              formatChartAxisLabel(
                value,
                props.xAxisField,
                xAxisFieldDescriptor,
              ),
          }
        : null,
    axisLeft: props.showAxisY
      ? {
          ...commonAxisConfig,
          format: value =>
            formatChartAxisLabel(value, props.yAxisField, yAxisFieldDescriptor),
        }
      : null,

    label: d => getMetricValue(d.data).fullString,
    labelTextColor: ({ value }) => {
      if (!value || value === 0) {
        return colors.app.background;
      }
      return 'RGB(0,0,0)';
    },
    colors: datum => {
      // if (_.isEqual(datum.data._key, selectedSubgroup)) {
      //   return colors.brand.DEFAULT;
      // }
      return colorBy === 'bucket' ? datum.data._color : datum.data._groupColor;
    },
    borderColor: datum => {
      const key = _.pick(datum.data, [props.xAxisField, props.yAxisField]);
      if (_.isEqual(key, selectedSubgroup)) {
        return colors.app.background;
      }
      return colorBy === 'bucket' ? datum.data._color : colors.app.background;
    },
    borderWidth: 2,
    yInnerPadding: 0.1,
    yOuterPadding: 0.1,
    inactiveOpacity: 0.9,
    tooltip: datum => (
      <ActualityAggregatedStatChartTooltip datum={datum.cell} />
    ),
    onClick: (cell, e) => onClick(cell.data),
    theme: commonThemeConfig,
  };

  return (
    <>
      <ContainerChart
        className={classNames(
          'bg-app-background',
          props.fullHeight && 'h-full overflow-auto',
          props.compareMode && 'overflow-auto',
        )}
        containerHeight={
          props.compareMode
            ? '50%'
            : props.compareMode && props.fullHeight
              ? '100%'
              : !props.compareMode && props.fullHeight
                ? '100%'
                : '100%'
        }

        // className={classNames(
        //   props.fullHeight && 'overflow-auto',
        //   props.compareMode && 'overflow-auto',
        // )}
        // containerHeight={
        //   props.compareMode
        //     ? '50%'
        //     : props.compareMode && props.fullHeight
        //       ? '50%'
        //       : !props.compareMode && props.fullHeight
        //         ? '90%'
        //         : '50%'
        // }
      >
        <ResponsiveHeatMapCanvas {...heatMapCanvasProps} />
      </ContainerChart>
    </>
  );
};

export default ActualityAggregatedStatsHeatmapChart;
